// firebase
import { doc, getFirestore, getDoc } from "firebase/firestore";

// @mui material components
import Grid from "@mui/material/Grid";

// Ajce Hostel App React components
import MDBox from "components/MDBox";

// Ajce Hostel App React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import StudentInfoCard from "examples/Cards/InfoCards/StudentInfoCard";

// Overview page components
import Header from "layouts/studentProfile/components/Header";
import InOutTimeline from "layouts/studentProfile/components/InOutTimeline";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import { CircularProgress } from "@mui/material";
import AddTimeLineModal from "./components/AddTimeLineModal";
import MDSnackbar from "components/MDSnackbar";
import BehaviourTimeline from "./components/BehaviourTimeline";
import AttendanceHistory from "./components/AttendanceHistory";

import { fetchBehaviourTimelineEntries } from "framework/students/profile";
import { toDateOnly } from "utils/date";

const firestore = getFirestore();

function StudentProfile() {
  const [isStudentDataLoading, setIsStudentDataLoading] = useState(false);
  const [fullScreenErr, setFullScreenErr] = useState(null);
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    admissionNo: "",
    roomNo: "",
    gender: "",
    branch: "",
    sem: "",
    idProofNo: "",
    regNo: "",
    dob: "",
    admissionDate: "",
    religion: "",
    caste: "",
    bloodGrp: "",
    mobNo: "",
    landLine: "",
    fatherName: "",
    fatherJob: "",
    fatherMob: "",
    motherName: "",
    motherJob: "",
    motherMob: "",
    guardianName: "",
    guardianRelation: "",
    guardianJob: "",
    guardianMob: "",
    presentAddr: "",
    permanentAddr: "",
    grantAvail: false,
    feeReduction: false,
    imgLink: "",
  });

  const [timelineData, setTimelineData] = useState([]);
  const [addTimelineModalOpen, setAddTimelineModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    desc: "",
    type: "success",
    icon: "check",
  });

  const { admissionNo } = useParams();

  useEffect(() => {
    (async () => {
      try {
        if (!admissionNo) throw new Error();
        setIsStudentDataLoading(true);

        const studentRef = doc(firestore, "students", admissionNo);
        const student = await getDoc(studentRef);
        if (!student.exists()) throw new Error();

        const studentData = student.data();

        studentData.admissionDate = toDateOnly(studentData.admissionDate?.toDate());
        studentData.dob = toDateOnly(studentData.dob?.toDate());

        setFormState((prev) => ({ ...prev, ...studentData }));
      } catch (err) {
        setFullScreenErr("Student you are trying to view not found, try again.");
      } finally {
        setIsStudentDataLoading(false);
      }
    })();
  }, [admissionNo, setIsStudentDataLoading, setFullScreenErr]);

  async function fetchBehaviourTimeline() {
    setTimelineData([]);
    try {
      const timelines = await fetchBehaviourTimelineEntries(formState.admissionNo);
      setTimelineData(timelines);
    } catch (err) {}
  }

  function closeSanckbar() {
    setSnackbar((prev) => ({ ...prev, desc: "" }));
  }

  function closeAddTimelineModal() {
    setAddTimelineModalOpen(false);
  }

  if (isStudentDataLoading || fullScreenErr) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mb={2} />
        <MDBox
          sx={{ height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          {isStudentDataLoading && !fullScreenErr && <CircularProgress color="primary" />}
          {fullScreenErr && !isStudentDataLoading && (
            <MDAlert color="error">
              <MDTypography variant="body2" color="white">
                {fullScreenErr}{" "}
                <Link to="/students" style={{ color: "lightblue" }}>
                  Go to Students List
                </Link>
              </MDTypography>
            </MDAlert>
          )}
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header item={formState}>
        <MDBox mt={5} mb={3} textAlign="center">
          <Grid container spacing={1}>
            <Grid item xs={12} md={12} xl={6} sx={{ display: "flex" }}>
              <StudentInfoCard
                title="general information"
                info={{
                  name: formState.name,
                  email: formState.email,
                  admissionNo: formState.admissionNo,
                  roomNo: formState.roomNo,
                  gender: formState.gender,
                  branch: formState.branch,
                  sem: formState.sem,
                  idProofNo: formState.idProofNo,
                  registerNo: formState.registerNo,
                  dob: formState.dob,
                  admissionDate: formState.admissionDate,
                  religion: formState.religion,
                  caste: formState.caste,
                  bloodGroup: formState.bloodGrp,
                }}
                shadow={false}
              />
            </Grid>
            <Grid item xs={12} md={12} xl={6} sx={{ display: "flex" }}>
              <StudentInfoCard
                title="contact information"
                info={{
                  mobNo: formState.mobNo,
                  landLine: formState.landLine,
                  fatherName: formState.fatherName,
                  fatherJob: formState.fatherJob,
                  fatherMob: formState.fatherMob,
                  motherName: formState.motherName,
                  motherJob: formState.motherJob,
                  motherMob: formState.motherMob,
                  guardianName: formState.guardianName,
                  guardianRelation: formState.guardianRelation,
                  guardianJob: formState.guardianJob,
                  guardianMob: formState.guardianMob,
                  presentAddr: formState.presentAddr,
                  permanentAddr: formState.permanentAddr,
                }}
                shadow={false}
              />
            </Grid>
          </Grid>
        </MDBox>
      </Header>
      <MDBox mt={5} mb={3}>
        {formState.admissionNo && (
          <Grid container spacing={1}>
            <Grid item xs={12} md={12} xl={4}>
              <BehaviourTimeline
                timelineData={timelineData}
                fetchBehaviourTimeline={fetchBehaviourTimeline}
                setAddTimelineModalOpen={setAddTimelineModalOpen}
              />
            </Grid>
            <Grid item xs={12} md={12} xl={4}>
              <AttendanceHistory admissionNo={formState.admissionNo} />
            </Grid>
            <Grid item xs={12} md={12} xl={4}>
              <InOutTimeline admissionNo={formState.admissionNo} />
            </Grid>
          </Grid>
        )}
      </MDBox>
      <AddTimeLineModal
        admissionNo={formState.admissionNo}
        open={addTimelineModalOpen && !!formState.admissionNo}
        closeModal={closeAddTimelineModal}
        setSnackbar={setSnackbar}
        fetchBehaviourTimeline={fetchBehaviourTimeline}
      />
      <MDSnackbar
        color={snackbar.type}
        icon={snackbar.icon}
        title="AJCE Hostel Management"
        dateTime=""
        content={snackbar.desc}
        open={!!snackbar.desc}
        onClose={closeSanckbar}
        close={closeSanckbar}
        bgWhite
      />
      <Footer />
    </DashboardLayout>
  );
}

export default StudentProfile;
